<template>
  <div
    class="owc-stage-cta-buttons"
    :class="{
      'owc-stage-cta-buttons--dark': isDarkMode,
    }"
  >
    <template v-if="isAutomatedDisplayMethod">
      <globalelem-cta-web-component
        class="owc-stage-cta-buttons__cta-wc"
        :modelseries="vehicleData.scoped ? vehicleData.modelSeries : ''"
        :subbrand="vehicleData.scoped ? vehicleData.subBrands : ''"
        :darkmode="isDarkMode"
        :direction="isMobile ? 'vertical' : 'horizontal'"
        size="m"
      ></globalelem-cta-web-component>
    </template>
    <template v-else>
      <a
        v-show="!!cta.label"
        v-for="cta in ctas"
        :key="cta.label"
        :target="cta.target"
        :href="cta.url"
        class="owc-stage-cta-buttons__button wb-button wb-button--medium"
        :class="[
          {
            'wb-button--theme-dark': isDarkMode,
            'wb-button--large': !isMobile,
          },
          getCtaButtonClasses(cta),
        ]"
      >
        {{ cta.label }}
      </a>
    </template>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { MediaQueryService, mobileBreakpoints, WB_RESIZE_EVENT_NAME } from '@owc-utils/MediaQueryService';
import { ButtonType } from '@owc-types/Button.enum';
import { BaseCtaLink } from '@owc-types/BaseCtaLink';
import { CtaButtonsDisplayMethod } from '@owc-utils/AutomatedCta/CtaAutomation.enum';
import {
  StageCtaButtonsComputed,
  StageCtaButtonsData,
  StageCtaButtonsMethods,
  StageCtaButtonsProps,
} from './StageCtaButtons.d';
import { isShimLoaded } from '@owc-utils/Shims/moduleShim';

/**
 * A CTA button mainly used in the stage component.
 * Provides the user with the possibility to do somethig based on a given context.
 */
export default Vue.extend<
  StageCtaButtonsData,
  StageCtaButtonsMethods,
  StageCtaButtonsComputed,
  StageCtaButtonsProps
>({
  name: 'owc-stage-cta-buttons',
  props: {
    buttons: {
      type: Array as () => Array<BaseCtaLink>,
      required: true,
    },
    cta: {
      type: Object,
      required: false,
    },
    isDarkMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    vehicleData: {
      type: Object,
      required: false,
    },
    scenario: {
      type: String,
      required: false,
    },
  },
  computed: {
    isAutomatedDisplayMethod() {
      return this.cta?.ctaDisplayMethod === CtaButtonsDisplayMethod.Automate;
    },
  },
  data() {
    return {
      wbMediaQueryService: new MediaQueryService(window),
      isMobile: true,
      ctas: [],
    };
  },
  async mounted() {
    // Used to change image ratio given a MediaQuery Event
    window.addEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
    this.wbMediaQueryService.observe(true);
    await this.setCtaButtons();
  },
  beforeDestroy() {
    window.removeEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
  },
  watch: {
    cta() {
      this.setCtaButtons();
    },
  },
  methods: {
    resizeEventHandler(resizeEvent) {
      this.isMobile = mobileBreakpoints.includes(resizeEvent.detail.current);
    },
    async setCtaButtons() {
      if (this.isAutomatedDisplayMethod && !isShimLoaded()) {
        if (!window.customElements.get('globalelem-cta-web-component')) {
          const { defineCustomElements } = await import('@dh-io-globalelem/cta-web-component/dist/loader');
          defineCustomElements(window);
        }
      } else {
        this.setCtaButtonsFromProps();
      }
    },
    setCtaButtonsFromProps() {
      const primaryButton = this.buttons.find(({ type }) => type === ButtonType.Primary);
      const secondaryButton = this.buttons.find(({ type }) => type === ButtonType.Secondary);
      // Due to workbench a primary button must to be on the right
      this.ctas = [secondaryButton, primaryButton].filter(cta => cta) as BaseCtaLink[];
    },
    getCtaButtonClasses(cta) {
      if (cta.type === ButtonType.Primary) {
        return 'wb-button--primary owc-stage-cta-buttons__button--primary';
      }
      return 'wb-button--secondary owc-stage-cta-buttons__button--secondary';
    },
  },
});
</script>
<style lang="scss">
@import '@owc-styles/utils/rtl-utils';
@import '@owc-styles/utils/theming';

$StageCtaModuleName: 'owc-stage-cta-buttons';

.#{$StageCtaModuleName} {
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  width: 100%;
  padding: var(--wb-spacing-m) 0;

  &__button {
    width: 100%;

    margin-top: var(--wb-spacing-xxs);

    white-space: nowrap;

    @include breakpoint-from(mq3) {
      flex: 1;
      margin-top: 0;
      margin-right: var(--wb-spacing-xxs);
    }
    @include breakpoint-from(mq4) {
      flex: 0 0 auto;
      width: auto;
    }
  }

  @include breakpoint-from(mq5) {
    padding: var(--wb-spacing-s) 0;
  }

  &__cta-wc {
    @include breakpoint-to(mq3) {
      flex-grow: 1;

      .globalelem-cta-web-component__btn-group {
        display: flex;

        > .wb-button {
          flex-grow: 1;
        }
      }
    }
  }
}
.owc-feature-stage {
  @include breakpoint-from(mq4) {
    .#{$StageCtaModuleName}__button {
      flex: 0 0 auto;
    }
  }
}

@include rtl-rule('.owc-stage-cta-buttons', false) {
  @include breakpoint-from(mq3) {
    &__button {
      margin-right: 0;
      margin-left: var(--wb-spacing-xxs);
    }
  }
}
</style>
